import JumboCardQuick from '@jumbo/components/JumboCardQuick';
import { List, PictureAsPdf } from '@mui/icons-material';
import DeleteIcon from '@mui/icons-material/Delete';
import {
    Button,
    ButtonGroup,
    CircularProgress,
    FormControl,
    Grid,
    TextField
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { generateSubjectGroupsPDFAPI, getReportSubjectGroups } from 'api/report';
import { getDateStringYYYYMMDD } from 'app/utils/appHelpers';
import 'dayjs/locale/tr';
import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Autocomplete from '@mui/material/Autocomplete';
import LoadingPopUp from 'app/components/LoadingPopup';

function ReportFilterCard({
    title,
    setFilterData,
    handleResultList: customHandleResultList,
    generatePDFApi: customGeneratePDFApi,
    showUnitFilter = true
}) {
    const defaultStartDate = new Date();
    defaultStartDate.setDate(1);
    defaultStartDate.setMonth(0);
    const defaultFinishedDate = new Date();
    const authUser = useSelector(state => state.auth.authUser);
    const allAuthorizedInsts = useSelector(state => state.auth.allAuthorizedInsts);
    const filteredUnitList = useSelector(state => state.auth.allAuthorizedUnits);

    const [state, setState] = useState({
        insts: allAuthorizedInsts, // Tüm yetkili kurumlar default seçili
        unitId: [],               // Unit seçimi boş
        startedDate: defaultStartDate,
        finishedDate: defaultFinishedDate,
    });
    const [loading, setLoading] = useState(false);
    const [lastFilter, setLastFilter] = useState(null);

    const [filteredUnits, setFilteredUnits] = useState([]);

    useEffect(() => {
        if (state.insts && state.insts.length > 0) {
            const newFilteredUnits = filteredUnitList.filter(unit =>
                state.insts.includes(unit.inst)
            );
            setFilteredUnits(newFilteredUnits);
        } else {
            setState(prevState => ({ ...prevState, insts: allAuthorizedInsts }));
            setFilteredUnits(filteredUnitList);
        }
    }, [state.insts, filteredUnitList, allAuthorizedInsts]);

    const handleInstChange = (event, newValue) => {
        if (newValue.length === 0) {
            setState(prevState => ({ ...prevState, insts: allAuthorizedInsts, unitId: [] }));
        } else {
            setState(prevState => ({ ...prevState, insts: newValue, unitId: [] }));
        }
    };

    const handleResultList = async () => {
        const newStartDate = getDateStringYYYYMMDD(state.startedDate, "00:00:01");
        const newFinishedDate = getDateStringYYYYMMDD(state.finishedDate, "23:59:59");

        const unitIds = state.unitId.map(unit => unit.id);
        const filters = {
            inst: state.insts.join(','),
            unit: unitIds.length > 0 ? unitIds.join(',') : null,
            startedDate: newStartDate,
            finishedDate: newFinishedDate
        };

        setLoading(true);
        try {
            // Eğer özel fonksiyon varsa onu kullan, yoksa varsayılanı kullan
            if (customHandleResultList) {
                await customHandleResultList(filters);
            } else {
                const res = await getReportSubjectGroups(filters);
                setFilterData(res.data.data);
            }
            setLastFilter(filters);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const generatePDFApi = async () => {
        if (!lastFilter) return;
        setLoading(true);
        try {
            // Eğer özel PDF fonksiyonu varsa onu kullan, yoksa varsayılanı kullan
            if (customGeneratePDFApi) {
                await customGeneratePDFApi();
            } else {
                await generateSubjectGroupsPDFAPI(lastFilter);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleClear = () => {
        setState({
            insts: allAuthorizedInsts,
            unitId: [],
            startedDate: defaultStartDate,
            finishedDate: defaultFinishedDate,
        });
        setLastFilter(null);
        setFilterData([]);
    };

    return (
        <>
            <LoadingPopUp open={loading} />
            {!loading && (
                <JumboCardQuick title={title}>
                    <Grid container spacing={1} mb={1}>
                        <Grid item xs={2}>
                            <Autocomplete
                                multiple
                                size="small"
                                id="inst-autocomplete"
                                options={allAuthorizedInsts}
                                value={state.insts}
                                onChange={handleInstChange}
                                getOptionLabel={(option) => option}
                                renderInput={(params) => (
                                    <TextField {...params} label="Kurum" variant="outlined" size="small" />
                                )}
                            />
                        </Grid>

                        {/* Unit filtresi sadece showUnitFilter true ise göster */}
                        {showUnitFilter && (
                            <Grid item xs={3}>
                                <Autocomplete
                                    multiple
                                    size="small"
                                    id="unit-autocomplete"
                                    options={filteredUnits}
                                    value={state.unitId}
                                    onChange={(event, newValue) => {
                                        setState(prevState => ({ ...prevState, unitId: newValue }));
                                    }}
                                    getOptionLabel={(option) => option.name}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Kurul/Kol/Komisyon" variant="outlined" size="small" />
                                    )}
                                />
                            </Grid>
                        )}

                        {/* Tarih Seçimleri */}
                        <Grid item xs={2}>
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="tr">
                                <DatePicker
                                    label="Başlangıç"
                                    size="small"
                                    value={state.startedDate}
                                    onChange={(newValue) =>
                                        setState(prevState => ({ ...prevState, startedDate: newValue }))
                                    }
                                    renderInput={(params) => <TextField {...params} size="small" />}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={2}>
                            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="tr">
                                <DatePicker
                                    label="Bitiş"
                                    size="small"
                                    value={state.finishedDate}
                                    onChange={(newValue) =>
                                        setState(prevState => ({ ...prevState, finishedDate: newValue }))
                                    }
                                    renderInput={(params) => <TextField {...params} size="small" />}
                                />
                            </LocalizationProvider>
                        </Grid>

                        {/* İşlem Butonları */}
                        <Grid item xs={2}>
                            <ButtonGroup>
                                <Button variant="outlined" size="medium" color="success" startIcon={<List />} onClick={handleResultList}>
                                    Listele
                                </Button>
                                <Button
                                    variant="outlined"
                                    size="medium"
                                    color="success"
                                    startIcon={<PictureAsPdf />}
                                    disabled={!lastFilter}
                                    onClick={generatePDFApi}
                                >
                                    PDF
                                </Button>
                                <Button variant="outlined" size="medium" color="success" startIcon={<DeleteIcon />} onClick={handleClear}>
                                    TEMİZLE
                                </Button>
                            </ButtonGroup>
                        </Grid>
                    </Grid>
                </JumboCardQuick>
            )}
        </>
    );
}

export default ReportFilterCard;
